import React, { useState, useEffect, useContext, useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import { getSinceInstallationLabel } from "../../utils/installationDate";
import "./Page.css";
import "./ActionsCleaning.css";
import { SummaryStatCardsRow, formatSummaryStatDelta } from "../../components/v2/cards";
import LoadingIndicator from "../../components/v2/cards/LoadingIndicator";
import CleanLogo from "../../img/v2/clean-logo.svg";
import TowelLogo from "../../img/v2/towel-logo.svg";
import {
  getDemoHotel73CleaningStats,
  isDemoHotel73,
} from "../../utils/demoHotel73Stats";

const formatCurrency = (num, locale) => {
  if (num === null || num === undefined) return "--";
  return typeof num === "number"
    ? `${num.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
    : num;
};

const getRewardIcon = (rewardName, lang) => {
  if (!rewardName) return null;
  const nameLower = rewardName.toLowerCase();
  if (nameLower.includes("luniclean") || nameLower.includes("ménage") || nameLower.includes("menage")) {
    return <img src={CleanLogo} alt="LuniClean" className="cleaning-statistics__icon-img" />;
  }
  return <img src={TowelLogo} alt={translate("v2_cleaning_icon_towels", lang)} className="cleaning-statistics__icon-img" />;
};

const CleaningStatisticsTable = ({ data = [], loading = false }) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);

  if (loading) {
    return (
      <div className="cleaning-statistics__container">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="cleaning-statistics__container">
      <div className="cleaning-statistics__header">
        <div className="cleaning-statistics__header-cell">{translate("v2_actions_cleaning_table_service", lang)}</div>
        <div className="cleaning-statistics__header-cell cleaning-statistics__header-cell--no-bg cleaning-statistics__header-cell--center">
          {translate("v2_actions_cleaning_table_count", lang)}
        </div>
        <div className="cleaning-statistics__header-cell cleaning-statistics__header-cell--center">
          {translate("v2_actions_cleaning_table_gain", lang)}
        </div>
        <div className="cleaning-statistics__header-cell cleaning-statistics__header-cell--center">
          {translate("v2_actions_cleaning_table_savings", lang)}
        </div>
      </div>
      {!data || data.length === 0 ? (
        <div className="cleaning-statistics__empty-row">
          <div className="cleaning-statistics__cell-empty">{translate("v2_no_data", lang)}</div>
        </div>
      ) : (
        <div className="cleaning-statistics__body">
          {data.map((item, index) => (
            <div key={item.id || index} className="cleaning-statistics__row">
              <div className="cleaning-statistics__cell cleaning-statistics__cell-reward">
                <div className="cleaning-statistics__icon">{getRewardIcon(item.rewardName, lang)}</div>
                <span className="cleaning-statistics__reward-name">{item.rewardName || "--"}</span>
              </div>
              <div className="cleaning-statistics__cell cleaning-statistics__cell--no-bg cleaning-statistics__cell--center">
                <span className="cleaning-statistics__value-box">
                  {item.servicesDeclined !== null && item.servicesDeclined !== undefined
                    ? item.servicesDeclined.toString()
                    : "--"}
                </span>
              </div>
              <div className="cleaning-statistics__cell cleaning-statistics__cell-gain cleaning-statistics__cell--center">
                <span className="cleaning-statistics__value-box">
                  {formatCurrency(item.actSavingsEuro, locale)}
                </span>
              </div>
              <div className="cleaning-statistics__cell cleaning-statistics__cell-euros cleaning-statistics__cell--center">
                <span className="cleaning-statistics__value-box">{formatCurrency(item.eurosSaved, locale)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatLastSentDate = (value) => {
  if (!value) return "--";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = String(value.getUTCDate()).padStart(2, "0");
    const month = String(value.getUTCMonth() + 1).padStart(2, "0");
    const year = value.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }

  return "--";
};

const ServicesDeclinedSummaryTable = ({ data = [], loading = false }) => {
  const { lang } = useContext(HotelContext);

  if (loading) {
    return (
      <div className="cleaning-statistics__container cleaning-statistics__container-summary">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="cleaning-statistics__container cleaning-statistics__container-summary">
      <div className="cleaning-statistics__header cleaning-statistics__header-summary">
        <div className="cleaning-statistics__header-cell cleaning-statistics__header-cell--left">
          {translate("v2_actions_cleaning_recipient", lang)}
        </div>
        <div className="cleaning-statistics__header-cell cleaning-statistics__header-cell--center">
          {translate("v2_actions_cleaning_language", lang)}
        </div>
        <div className="cleaning-statistics__header-cell cleaning-statistics__header-cell--right">
          {translate("v2_actions_cleaning_last_sent", lang)}
        </div>
      </div>
      {!data || data.length === 0 ? (
        <div className="cleaning-statistics__empty-row cleaning-statistics__empty-row-summary">
          <div className="cleaning-statistics__cell-empty">{translate("v2_actions_cleaning_no_recipients", lang)}</div>
        </div>
      ) : (
        <div className="cleaning-statistics__body">
          {data.map((item, index) => (
            <div key={item.id || index} className="cleaning-statistics__row cleaning-statistics__row-summary">
              <div className="cleaning-statistics__cell cleaning-statistics__cell--left">
                <span className="cleaning-statistics__recipient-email">{item.email || "--"}</span>
              </div>
              <div className="cleaning-statistics__cell cleaning-statistics__cell--center">
                <span className="cleaning-statistics__recipient-language">{item.language || "--"}</span>
              </div>
              <div className="cleaning-statistics__cell cleaning-statistics__cell--right">
                <span className="cleaning-statistics__recipient-date">
                  {formatLastSentDate(item.lastSentAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ActionsCleaning = () => {
  const keycloak = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const [cleaningStats, setCleaningStats] = useState({
    servicesDeclined: { value: 0, variation: 0 },
    amountSaved: { value: 0, variation: 0 },
    servicesByType: [],
    servicesDeclinedSummary: [],
  });

  const fetchCleaningStats = () => {
    if (!keycloak || !keycloak.isAuthenticated || !keycloak.user?.access_token || !hId) {
      setHasError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    if (isDemoHotel73(hId)) {
      setCleaningStats(getDemoHotel73CleaningStats());
      setHasError(false);
      setLoading(false);
      return;
    }

    fetch(`/api/hotels/${hId}/cleaning/stats?lang=${lang}`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errData) => {
            throw new Error(errData.message || `HTTP error! status: ${response.status}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        setCleaningStats(data);
        setHasError(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("ActionsCleaning - Error fetching data:", error);
        setHasError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (keycloak && keycloak.isAuthenticated && keycloak.user && hId) {
      fetchCleaningStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.isAuthenticated, hId, lang]);

  const card1Data = cleaningStats.servicesDeclined || { value: 0, variation: 0 };
  const card2Data = cleaningStats.amountSaved || { value: 0, variation: 0 };
  const tableData = cleaningStats.servicesByType || [];
  const summaryTableData = cleaningStats.servicesDeclinedSummary || [];
  const summarySubtitle = getSinceInstallationLabel(cleaningStats.installationDate, lang, locale);

  const summaryCards = useMemo(
    () => [
      {
        id: "services-declined",
        title: translate("v2_actions_cleaning_services_declined", lang),
        subtitle: summarySubtitle,
        value: Math.round(card1Data.value || 0).toLocaleString(locale),
        deltaValue: formatSummaryStatDelta(card1Data.variation, "", locale),
        accentColor: "#3C5544",
        loading,
      },
      {
        id: "financial-savings",
        title: translate("v2_dashboard_financial_savings", lang),
        subtitle: summarySubtitle,
        value: Math.round(card2Data.value || 0).toLocaleString(locale),
        valueUnit: "€",
        deltaValue: formatSummaryStatDelta(card2Data.variation, "€", locale),
        accentColor: "#FAA54B",
        loading,
      },
    ],
    [card1Data.value, card1Data.variation, card2Data.value, card2Data.variation, loading, summarySubtitle, lang, locale]
  );

  return (
    <div className="v2-page">
      <div className="v2-page__header">
        <h1>{translate("v2_actions_cleaning_title", lang)}</h1>
      </div>
      {hasError && (
        <div style={{ padding: "20px", textAlign: "center", color: "#d32f2f" }}>
          <p>{translate("v2_error_load_data", lang)}</p>
        </div>
      )}
      <div className="actions-cleaning__content">
        <div className="actions-cleaning__stats-top">
          <SummaryStatCardsRow cards={summaryCards} />
        </div>

        <div className="actions-cleaning__card actions-cleaning__card-large">
          <div className="monetary-card__header">
            <h3 className="monetary-card__title">{translate("v2_actions_cleaning_section_declined", lang)}</h3>
            <p className="monetary-card__subtitle">{translate("v2_actions_cleaning_section_total", lang)}</p>
          </div>
          <CleaningStatisticsTable data={tableData} loading={loading} />
        </div>

        <div className="actions-cleaning__card actions-cleaning__card-large">
          <div className="monetary-card__header">
            <h3 className="monetary-card__title">{translate("v2_actions_cleaning_section_recipients", lang)}</h3>
            <p className="monetary-card__subtitle">
              {translate("v2_actions_cleaning_section_recipients_sub", lang)}
            </p>
          </div>
          <ServicesDeclinedSummaryTable data={summaryTableData} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default ActionsCleaning;
